import { Component, Input, Output, EventEmitter, inject, OnInit, Optional } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule, FormsModule, FormControl, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { SharedComponentsModule } from '../../shared-components/shared-components.module';
import {
  ShoppingCartPaymentProvider,
  ShoppingCartPaymentData,
  PaymentProcessResult,
  SHOPPING_CART_PAYMENT_PROVIDER,
  PaymentFormFieldConfig
} from './shopping-cart-payment.types';
import { ShoppingCartItem, CartItemType } from '../shopping-cart.types';
import { CathyService } from '@sbd-doug-frontend/app/services/cathy.service';

@Component({
  selector: 'cipx-shopping-cart-payment',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatCheckboxModule,
    MatIconModule,
    MatButtonModule,
    SharedComponentsModule
  ],
  template: `
    <form [formGroup]="paymentForm" *ngIf="paymentForm" autocomplete="on">
      <div class="p-2">
        <h2 class="text-lg font-semibold text-gray-800 dark:text-gray-400">Payment</h2>
        <p class="text-sm text-gray-500 dark:text-gray-100">All transactions are secured and encrypted.</p>

        <!-- Payment Method -->
        <div class="mt-4 border rounded-lg shadow-sm bg-gray-50 dark:bg-transparent">
          <div class="flex items-center justify-between p-4 border bg-blue-50 border-blue-700 rounded-lg rounded-b-none dark:bg-brandOrange dark:text-white">
            <mat-radio-group class="flex items-center space-x-3">
              <mat-radio-button class="text-blue-600 dark:text-white focus:ring-blue-500" checked="true"></mat-radio-button>
              <span class="font-medium text-gray-800 dark:text-white">Credit or debit card</span>
            </mat-radio-group>
            <div class="flex space-x-2">
              <div class="bg-white rounded-lg p-2" [ngClass]="{'border-2 border-blue-700': selectedCardType === 'visa'}">
                <img src="assets/cards/visa.svg" alt="Visa" class="h-5">
              </div>
              <div class="bg-white rounded-lg p-2" [ngClass]="{'border-2 border-blue-700': selectedCardType === 'mastercard'}">
                <img src="assets/cards/mastercard.svg" alt="MasterCard" class="h-5">
              </div>
              <div class="bg-white rounded-lg p-2 opacity-40" title="American Express not currently accepted">
                <img src="assets/cards/amex.svg" alt="Amex" class="h-5">
              </div>
            </div>
          </div>

          <div class="px-4 pt-3 pb-1 text-sm text-gray-700 dark:text-gray-200">
            <p>
              Due to planned updates, we currently cannot accept American Express.
              <button
                type="button"
                class="text-blue-700 dark:text-blue-300 underline font-medium hover:text-blue-900 dark:hover:text-blue-200"
                (click)="amexWaitInfoExpanded = !amexWaitInfoExpanded"
                [attr.aria-expanded]="amexWaitInfoExpanded">
                Can I wait and use my Amex later?
              </button>
            </p>
            <div *ngIf="amexWaitInfoExpanded" class="mt-3 mb-2 p-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 space-y-2">
              <h3 class="font-semibold text-gray-800 dark:text-gray-100">Wait and Pay with Amex</h3>
              <p>The restriction on American Express processing is temporary.</p>
              <p>
                The fastest route is to use an alternate payment method now, but if you'd like to wait, you can:
              </p>
              <ul class="list-disc pl-5 space-y-1">
                <li>Try the link you clicked after August 14th when we expect processing to resume.</li>
                <li>
                  Reply to our email or email us and simply ask us to call, email, or text you once Amex is ready
                  (select all that apply).
                </li>
              </ul>
            </div>
          </div>

          <!-- Card Details -->
          <div class="p-4 space-y-4">
            <mat-form-field class="w-full my-2" [subscriptSizing]="'dynamic'">
              <input
                matInput
                formControlName="credit_card_number"
                [placeholder]="'Card number'"
                autocomplete="cc-number"
              />
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                   stroke="currentColor"
                   class="size-6">
                <path stroke-linecap="round" stroke-linejoin="round"
                      d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
              </svg>
            </mat-form-field>

            <div class="flex space-x-4">
              <mat-form-field class="w-20 my-2" [subscriptSizing]="'dynamic'">
                <input
                  matInput
                  #expirationMonthInput
                  [value]="expirationMonth"
                  (input)="onExpirationMonthInput(expirationMonthInput.value)"
                  (change)="onExpirationFieldsChange(expirationMonthInput.value, expirationYearInput.value)"
                  placeholder="MM"
                  autocomplete="cc-exp-month"
                  maxlength="2"
                  inputmode="numeric"
                  pattern="(0[1-9]|1[0-2])"
                />
              </mat-form-field>
              <span class="self-center text-gray-500 dark:text-gray-400">/</span>
              <mat-form-field class="w-20 my-2" [subscriptSizing]="'dynamic'">
                <input
                  matInput
                  #expirationYearInput
                  [value]="expirationYear"
                  (input)="onExpirationYearInput(expirationYearInput.value)"
                  (change)="onExpirationFieldsChange(expirationMonthInput.value, expirationYearInput.value)"
                  placeholder="YY"
                  autocomplete="cc-exp-year"
                  maxlength="2"
                  inputmode="numeric"
                  pattern="[0-9]{2}"
                />
              </mat-form-field>

              <mat-form-field class="flex-1 my-2" [subscriptSizing]="'dynamic'">
                <input
                  matInput
                  formControlName="credit_card_cvv"
                  type="text"
                  [placeholder]="'Security code'"
                  autocomplete="cc-csc"
                  maxlength="4"
                  pattern="[0-9]*"
                  inputmode="numeric"
                />
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                     stroke="currentColor" class="size-6">
                  <path stroke-linecap="round" stroke-linejoin="round"
                        d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
                </svg>
              </mat-form-field>
            </div>

            <mat-form-field class="w-full my-2" [subscriptSizing]="'dynamic'">
              <input
                matInput
                formControlName="payer_name"
                [placeholder]="'Name on card'"
                autocomplete="cc-name"
              />
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                   stroke="currentColor" class="size-6">
                <path stroke-linecap="round" stroke-linejoin="round"
                      d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
              </svg>
            </mat-form-field>
          </div>
        </div>
      </div>

      <!-- Billing Address -->
      <div class="p-4">
        <h2 class="text-lg font-semibold text-gray-800 dark:text-gray-400">Billing Address</h2>
        <p class="text-sm text-gray-500 dark:text-gray-100">Select the address that matches your card or payment method.</p>

        <!-- Billing Address Options -->
        <mat-radio-group formControlName="useDefaultAddress" class="mt-4">
          <mat-radio-button class="flex items-center space-x-3 p-3 border rounded-lg rounded-b-none shadow-sm cursor-pointer dark:text-white"
                            [ngClass]="{'bg-blue-50 dark:bg-brandOrange border-blue-700' : paymentForm.get('useDefaultAddress')?.value}"
                            [value]="true">
            &nbsp;<span class="text-gray-800 dark:text-white">Same as default address</span>
          </mat-radio-button>
          <mat-radio-button
            class="flex items-center space-x-3 p-3 border rounded-lg rounded-t-none shadow-sm cursor-pointer"
            [ngClass]="{'bg-blue-50 dark:bg-brandOrange border-blue-700' : !paymentForm.get('useDefaultAddress')?.value}"
            [value]="false">
            &nbsp;<span class="text-gray-800 dark:text-white">Use a different billing address</span>
          </mat-radio-button>
        </mat-radio-group>

        <!-- Conditional Billing Address Form -->
        <div *ngIf="!paymentForm.get('useDefaultAddress')?.value" class="mt-6 space-y-4">
          <sbd-app-address-form [showCounty]="false" [formGroup]="paymentForm">
          </sbd-app-address-form>
        </div>
      </div>

      <!-- Signature Confirmation -->
      <div *ngIf="signatureCheckboxLabel" class="p-4">
        <mat-checkbox formControlName="signatureRequired" [color]="'primary'" class="cipx-checkbox-hover-brand-green">
          <span class="text-sm text-gray-700 dark:text-gray-300 whitespace-normal leading-tight">
            {{ signatureCheckboxLabel }}
          </span>
        </mat-checkbox>
      </div>

      <!-- Processing Status -->
      <div *ngIf="isProcessing" class="mt-4 p-4 bg-blue-50 dark:bg-blue-900 rounded-lg">
        <div class="flex items-center space-x-2">
          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span class="text-blue-800 dark:text-blue-200">Processing payment...</span>
        </div>
      </div>

      <!-- Error Message -->
      <div *ngIf="errorMessage" class="mt-4 p-4 bg-red-50 dark:bg-red-900 rounded-lg">
        <div class="flex items-center space-x-2">
          <mat-icon class="text-red-600 dark:text-red-400">error</mat-icon>
          <span class="text-red-800 dark:text-red-200">{{ errorMessage }}</span>
        </div>
      </div>
    </form>

    <!-- No Payment Provider -->
    <div *ngIf="!paymentProvider" class="p-4 bg-yellow-50 dark:bg-yellow-900 rounded-lg">
      <div class="flex items-center space-x-2">
        <mat-icon class="text-yellow-600 dark:text-yellow-400">warning</mat-icon>
        <span class="text-yellow-800 dark:text-yellow-200">
          Payment provider not configured for this application.
        </span>
      </div>
    </div>
  `,
  styleUrl: './shopping-cart-payment.component.scss'
})
export class CIPXShoppingCartPaymentComponent implements OnInit {
  private _items: ShoppingCartItem<any>[] = [];
  @Input() set items(value: ShoppingCartItem<any>[]) {
    this._items = value;
    this.updateSignatureRequiredControl();
  }
  get items(): ShoppingCartItem<any>[] {
    return this._items;
  }
  @Output() paymentProcessed = new EventEmitter<PaymentProcessResult>();
  @Output() formValid = new EventEmitter<boolean>();

  public paymentForm: FormGroup | null = null;
  public formConfig: PaymentFormFieldConfig | null = null;
  public selectedCardType = '';
  public isProcessing = false;
  public errorMessage = '';
  public amexWaitInfoExpanded = false;

  /** Display values for split expiration fields; synced with expiration_date control (MM/YY). */
  public expirationMonth = '';
  public expirationYear = '';

  public get signatureCheckboxLabel(): string {
    const quoteRequests = this.items.filter(item => item.type === CartItemType.QUOTE_REQUEST);

    if (quoteRequests.length === 0) {
      return '';
    }

    if (quoteRequests.length === 1) {
      const bond = quoteRequests[0].data;
      if (+bond?.quote_request_bond?.bond_family_id === 51) {
        return '';
      }

      const signatureOptionId = bond?.selected_quote?.signature_option_id;

      if (signatureOptionId === 1) {
        return 'I understand that a signed application may be required by the surety company before or after your bond is issued.';
      } else {
        return 'I understand that a completed, signed application is required as a condition of obtaining this bond. If the bond is issued before the signed application is received, I agree to promptly provide the signed application upon request. Failure to provide the signed application when requested may result in the immediate cancellation of the bond, subject to any applicable legal or regulatory notice requirements.';
      }
    }

    // More than one bond
    return 'I understand that a completed, signed application may be required for each bond as a condition of obtaining the bond. If any bond is issued before the required signed application is received, I agree to promptly provide the signed application upon request. Failure to provide the signed application when requested may result in the immediate cancellation of the bond, subject to any applicable legal or regulatory notice requirements.';
  }

  private _paymentProvider = inject(SHOPPING_CART_PAYMENT_PROVIDER, { optional: true });
  private _cathyService = inject(CathyService);

  get paymentProvider() {
    return this._paymentProvider;
  }

  ngOnInit() {
    if (this._paymentProvider) {
      this.paymentForm = this._paymentProvider.createPaymentForm();
      this.formConfig = this._paymentProvider.getFormFieldConfig();

      this.updateSignatureRequiredControl();

      // Listen for form validity changes
      this.paymentForm.statusChanges.subscribe(status => {
        const isValid = status === 'VALID';
        this.formValid.emit(isValid);
      });

      // Emit initial validity
      this.formValid.emit(this.paymentForm.valid);

      // Listen for card number changes to update card type
      this.paymentForm.get('credit_card_number')?.valueChanges.subscribe(() => {
        this.onCardNumberChange();
      });

      // Keep expiration_date (MM/YY) in sync with split month/year display and normalize legacy autofill
      const expirationControl = this.paymentForm.get('expiration_date');
      if (expirationControl) {
        const syncExpirationFromControl = (value: string) => {
          const normalized = this.normalizeExpirationDate(value);
          if (normalized !== value) {
            expirationControl.setValue(normalized, { emitEvent: false });
          }
          const { month, year } = this.parseExpirationDate(normalized);
          this.expirationMonth = month;
          this.expirationYear = year;
        };
        expirationControl.valueChanges.subscribe((value: string) => syncExpirationFromControl(value ?? ''));
        const initial = expirationControl.value;
        if (initial != null && initial !== '') {
          syncExpirationFromControl(String(initial));
        }
      }
    }
  }

  onExpirationMonthInput(value: string): void {
    const digits = value.replace(/\D/g, '').slice(0, 2);
    this.expirationMonth = digits;
    this.mergeExpirationIntoControl();
  }

  onExpirationYearInput(value: string): void {
    const digits = value.replace(/\D/g, '').slice(0, 2);
    this.expirationYear = digits;
    this.mergeExpirationIntoControl();
  }

  /** Sync from DOM when browser autofill only fires change (e.g. cc-exp-month / cc-exp-year). */
  onExpirationFieldsChange(monthValue: string, yearValue: string): void {
    const month = String(monthValue ?? '').replace(/\D/g, '').slice(0, 2);
    const year = String(yearValue ?? '').replace(/\D/g, '').slice(-2);
    if (month !== this.expirationMonth || year !== this.expirationYear) {
      this.expirationMonth = month;
      this.expirationYear = year;
      this.mergeExpirationIntoControl();
    }
  }

  private mergeExpirationIntoControl(): void {
    const control = this.paymentForm?.get('expiration_date');
    if (!control) return;
    const month = this.expirationMonth.trim();
    const year = this.expirationYear.trim();
    const merged = month || year ? `${month}/${year}`.replace(/\/+$/, '') : '';
    control.setValue(merged, { emitEvent: true });
  }

  private parseExpirationDate(value: string | null | undefined): { month: string; year: string } {
    if (value == null || typeof value !== 'string') return { month: '', year: '' };
    const parts = value.trim().split(/\D/).filter(Boolean);
    return {
      month: (parts[0] ?? '').slice(0, 2),
      year: (parts[1] ?? '').slice(-2)
    };
  }

  /**
   * Normalize expiration input to MM/YY. Handles autofill that uses full year (2030) or MM/YYYY (12/2030).
   */
  private normalizeExpirationDate(value: string | null | undefined): string {
    if (value == null || typeof value !== 'string') return value ?? '';
    const trimmed = value.trim();
    if (!trimmed) return trimmed;

    // Exactly 4 digits (e.g. "2030") → treat as YY from last 2 digits → "01/30"
    const fourDigitYear = /^\d{4}$/;
    if (fourDigitYear.test(trimmed)) {
      return '01/' + trimmed.slice(-2);
    }

    // MM/YYYY or MM-YYYY (e.g. "12/2030", "01-2025") → MM/YY
    const mmFullYear = /^(0[1-9]|1[0-2])\D?(\d{4})$/;
    const mmFullMatch = trimmed.match(mmFullYear);
    if (mmFullMatch) {
      return mmFullMatch[1] + '/' + mmFullMatch[2].slice(-2);
    }

    return value;
  }

  onCardNumberChange() {
    if (!this._paymentProvider || !this.paymentForm) return;

    const cardNumber = this.paymentForm.get('credit_card_number')?.value || '';
    const validation = this._paymentProvider.validateCardNumber(cardNumber);

    this.selectedCardType = validation.cardType;
    this.paymentForm.get('selectedCardType')?.setValue(validation.cardType);
  }

  // Function to validate card number and set the card type (for compatibility)
  validateCardNumber(): { [key: string]: boolean } | null {
    const value = this.paymentForm!.get('credit_card_number')?.value;
    if (!value) {
      this.paymentForm!.get('selectedCardType')?.setValue('');
      return null;
    }

    if (this._paymentProvider) {
      const validation = this._paymentProvider.validateCardNumber(value);
      this.selectedCardType = validation.cardType;
      this.paymentForm!.get('selectedCardType')?.setValue(validation.cardType);
      return validation.isValid ? null : { invalidCard: true };
    }

    return null;
  }

  processPayment() {
    if (!this._paymentProvider || !this.paymentForm || this.paymentForm.invalid) {
      return;
    }

    this.isProcessing = true;
    this.errorMessage = '';

    const formData = this.paymentForm.value;

    // Add signature acknowledgment text if present
    if (this.signatureCheckboxLabel) {
      formData.signature_acknowledgment_text = this.signatureCheckboxLabel;
    }

    const quoteRequestIDs = this.items
      .map(item => item.data?.quote_request_id)
      .filter(id => id !== undefined && id !== null);

    const paymentData: ShoppingCartPaymentData = {
      items: this.items,
      subtotal: this.items.reduce((total, item) => total + item.getSubtotal(), 0),
      tax: this.items.reduce((total, item) => total + item.getTax(), 0),
      shipping: this.items.reduce((total, item) => total + item.getShipping(), 0),
      total: this.items.reduce((total, item) => total + item.getTotalWithTaxAndShipping(), 0),
      quoteRequestIDs: quoteRequestIDs
    };

    this._paymentProvider!.processPayment(formData, paymentData).subscribe({
      next: (result) => {
        this.isProcessing = false;
        this.paymentProcessed.emit(result);

        if (!result.success && result.error) {
          this.errorMessage = result.error;
          this._cathyService.error(result.error)
        }
      },
      error: (error) => {
        this.isProcessing = false;
        this.errorMessage = 'An error occurred while processing your payment. Please try again.';
        this._cathyService.error(this.errorMessage)
        this.paymentProcessed.emit({ success: false, error: error.message });
      }
    });
  }

  getPaymentData(): ShoppingCartPaymentData {
    const quoteRequestIDs = this.items
      .map(item => item.data?.quote_request_id)
      .filter(id => id !== undefined && id !== null);

    return {
      items: this.items,
      subtotal: this.items.reduce((total, item) => total + item.getSubtotal(), 0),
      tax: this.items.reduce((total, item) => total + item.getTax(), 0),
      shipping: this.items.reduce((total, item) => total + item.getShipping(), 0),
      total: this.items.reduce((total, item) => total + item.getTotalWithTaxAndShipping(), 0),
      quoteRequestIDs: quoteRequestIDs
    };
  }

  isFormValid(): boolean {
    return this.paymentForm ? this.paymentForm.valid : false;
  }

  private updateSignatureRequiredControl() {
    if (!this.paymentForm) return;

    if (this.signatureCheckboxLabel) {
      if (!this.paymentForm.contains('signatureRequired')) {
        this.paymentForm.addControl('signatureRequired', new FormControl(false, Validators.requiredTrue));
      }
    } else {
      if (this.paymentForm.contains('signatureRequired')) {
        this.paymentForm.removeControl('signatureRequired');
      }
    }
  }
}
